import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

/* =========================================================================
   FLOATING WHATSAPP BUTTON

   The message is pre-filled with the page the visitor was on when they
   tapped it. Whoever picks up the chat then knows whether they arrived from
   the drone page or the pricing section, without having to ask, which is the
   difference between a cold reply and a useful one.

   The number is read from site settings so an admin can change it in the
   portal rather than waiting on a deploy.
   ========================================================================= */

const API_BASE = import.meta.env.VITE_API_BASE || '/api'

/* Readable page names for the message. A raw path tells the person reading
   the chat far less than the name of the page does. */
const PAGE_NAMES = {
  '/': 'Home',
  '/services': 'Services',
  '/portfolio': 'Portfolio',
  '/about': 'About',
  '/careers': 'Careers',
  '/contact': 'Contact',
  '/how-we-work': 'How we work',
  '/faqs': 'FAQs',
  '/services/website-development': 'Website Development',
  '/services/app-development': 'App Development',
  '/services/software-development': 'Software Development',
  '/services/cyber-security': 'Cyber Security',
  '/services/drone-development': 'Drone Development',
  '/services/iot-development': 'IoT Development',
  '/services/ai-automation': 'AI & Automation',
  '/services/ui-ux-design': 'UI / UX Design',
  '/services/social-media-management': 'Social Media Management',
  '/services/digital-marketing': 'Digital Marketing',
  '/services/business-consulting': 'Business Consulting',
}

function pageName(pathname) {
  if (PAGE_NAMES[pathname]) return PAGE_NAMES[pathname]
  if (pathname.startsWith('/careers/')) return 'a job opening'
  if (pathname.startsWith('/portfolio/')) return 'a project case study'
  if (pathname.startsWith('/services/')) return 'a service page'
  return pathname
}

export default function WhatsAppButton() {
  const location = useLocation()
  const [cfg, setCfg] = useState(null)
  const [nudge, setNudge] = useState(false)

  useEffect(() => {
    let live = true
    fetch(`${API_BASE}/analytics.php?public=1`)
      .then((r) => r.json())
      .then((d) => { if (live && d.success) setCfg(d.whatsapp) })
      .catch(() => {
        /* If settings cannot be fetched the button simply does not appear,
           rather than opening a chat to a number that may be wrong. */
      })
    return () => { live = false }
  }, [])

  // A single, late, one-off nudge. Anything more insistent is an irritation.
  useEffect(() => {
    const t = setTimeout(() => setNudge(true), 12000)
    const h = setTimeout(() => setNudge(false), 22000)
    return () => { clearTimeout(t); clearTimeout(h) }
  }, [])

  const href = useMemo(() => {
    if (!cfg?.number) return null
    const name = pageName(location.pathname)
    const url = `${window.location.origin}${location.pathname}${location.search}`
    const text =
      `${cfg.greeting || 'Hi Spectranox, I would like to talk about a project.'}\n\n` +
      `Page: ${name}\n${url}`
    return `https://wa.me/${cfg.number}?text=${encodeURIComponent(text)}`
  }, [cfg, location.pathname, location.search])

  // Hidden inside the portal: a signed-in client has the messaging thread.
  if (!href || !cfg?.enabled || location.pathname.startsWith('/portal')) return null

  return (
    <div className="sx-wa-fab fixed bottom-5 right-5 z-[90] flex items-center gap-3 sm:bottom-6 sm:right-6">
      {nudge && (
        <span className="hidden animate-[wa-in_.3s_ease-out] rounded-2xl bg-white px-4 py-2.5 text-[13px] font-semibold text-slate-800 shadow-lg ring-1 ring-black/5 sm:block">
          Questions? Chat with us
        </span>
      )}

      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-no-modal
        aria-label="Chat with us on WhatsApp"
        /* 44px on a phone — the smallest a touch target may be and still be
           comfortably hit — growing on larger screens where there is room for
           it. The old 56px circle took a real bite out of a 375px viewport. */
        className="group relative grid size-11 place-items-center rounded-full bg-[#25D366] shadow-[0_6px_18px_-6px_rgba(37,211,102,0.5)] transition-transform hover:scale-105 active:scale-95 sm:size-[52px] lg:size-14"
      >
        {/* One slow halo, and only while the nudge is showing. The ring used to
            pulse forever, which is what made it hard to look away from. */}
        {nudge && (
          <span
            aria-hidden
            className="absolute inset-0 rounded-full bg-[#25D366] opacity-40 motion-safe:animate-[wa-halo_2.6s_ease-out_3]"
          />
        )}
        <svg viewBox="0 0 24 24" className="relative size-[22px] fill-white sm:size-6 lg:size-7" aria-hidden>
          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm0 18.17h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.4c0-4.54 3.7-8.23 8.25-8.23a8.23 8.23 0 0 1 8.24 8.24c0 4.54-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.13-1.05-.39-1.99-1.23-.74-.66-1.24-1.47-1.38-1.72-.15-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.09-.17.04-.31-.02-.43-.06-.13-.56-1.35-.77-1.84-.2-.49-.4-.42-.56-.43h-.47c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.11-.22-.17-.47-.29z" />
        </svg>
      </a>

      <style>{`@keyframes wa-in{from{opacity:0;transform:translateX(8px)}to{opacity:1;transform:none}}
        @keyframes wa-halo{0%{opacity:.4;transform:scale(1)}100%{opacity:0;transform:scale(1.5)}}`}</style>
    </div>
  )
}
